import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { logger } from "@/lib/logger";

// NOTE: The cart stores only non-sensitive shopping data (product_id, quantity,
// display fields, options). No auth tokens, PII, or payment data are kept here.
// Authentication remains in httpOnly cookies managed by the backend.
const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);
const KEY = "bolawpress_cart_v1";

const ALLOWED_KEYS = ["line_id", "product_id", "name", "unit_price", "image_url", "quantity", "options", "notes", "artwork_file_id", "artwork_filename"];

function sanitize(item) {
  const out = {};
  for (const k of ALLOWED_KEYS) if (k in item) out[k] = item[k];
  return out;
}

function loadCart() {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(KEY) : null;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(sanitize) : [];
  } catch (e) {
    logger.warn("Cart load failed:", e);
    return [];
  }
}

function saveCart(items) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(KEY, JSON.stringify(items.map(sanitize)));
  } catch (e) {
    logger.warn("Cart save failed:", e);
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);
  useEffect(() => { saveCart(items); }, [items]);
  const add = useCallback((product, quantity = 1, options = {}, extras = {}) =>
    setItems(prev => [...prev, sanitize({
      line_id: `${product.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      product_id: product.id, name: product.name, unit_price: product.base_price,
      image_url: product.image_url, quantity, options, notes: "",
      artwork_file_id: extras.artwork_file_id || null,
      artwork_filename: extras.artwork_filename || null,
    })]), []);
  const remove = useCallback((line_id) => setItems(prev => prev.filter(i => i.line_id !== line_id)), []);
  const updateArtwork = useCallback((line_id, artwork_file_id, artwork_filename) =>
    setItems(prev => prev.map(i => i.line_id === line_id ? sanitize({ ...i, artwork_file_id, artwork_filename }) : i)), []);
  const clear = useCallback(() => setItems([]), []);
  const subtotal = useMemo(() => items.reduce((s, i) => s + (i.unit_price * i.quantity), 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const value = useMemo(() => ({ items, add, remove, updateArtwork, clear, subtotal, count }), [items, add, remove, updateArtwork, clear, subtotal, count]);
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}
